import React, { useContext, useReducer, useEffect } from 'react';
import { Field, Form, FormSpy } from 'react-final-form';
import * as PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import toast from 'react-hot-toast';
import Select from 'react-select';
import {
  FormButtonToolbar,
  FormContainer,
  FormGroup,
  FormGroupField,
  FormGroupLabel,
} from '@/shared/components/form/FormElements';
import { Button } from '@/shared/components/Button';
import { AuthContext } from '../../../../../context/AuthContext';
 
// Define action types for the reducer
const ACTIONS = {
  SET_AMOUNT: 'set_amount',
  ADD_AMOUNT: 'add_amount',
  SET_PAYMENT_TYPE: 'set_payment_type',
  SET_FORM_VALUES: 'set_form_values',
};
 
// Initial state for the reducer
const initialState = {
  amount: 0,
  selectPaymentType: null,
  formValues: {
    recAmount: 0,
    receivedDate: new Date(),
    chequeDate: new Date(),
    narration: '',
    ref: '',
    bankName: '',
  },
};
 
// Reducer function to handle state updates
const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_AMOUNT:
      return {
        ...state,
        amount: action.payload,
        formValues: {
          ...state.formValues,
          recAmount: action.payload,
        },
      };
    case ACTIONS.ADD_AMOUNT:
       // eslint-disable-next-line
      const newAmount = state.amount + action.payload;
      return {
        ...state,
        amount: newAmount,
        formValues: {
          ...state.formValues,
          recAmount: newAmount,
        },
      };
    case ACTIONS.SET_PAYMENT_TYPE:
      return { ...state, selectPaymentType: action.payload };
    case ACTIONS.SET_FORM_VALUES:
      return { ...state, formValues: { ...state.formValues, ...action.payload } };
    default:
      return state;
  }
};
 
const AddAmount = ({ setShowAddAmountModal }) => {
  // Using useReducer instead of multiple useState calls
  const [state, dispatch] = useReducer(reducer, initialState);
  const { amount, selectPaymentType, formValues } = state;
 
  const { apiGlobal } = useContext(AuthContext);
  const { restaurantid, userId } = useContext(AuthContext);
 
  const handleAddAmount = (valueToAdd) => {
    dispatch({ type: ACTIONS.ADD_AMOUNT, payload: valueToAdd });
  };
 
  const handleInputChange = (e) => {
    const value = parseFloat(e.target.value);
    dispatch({ type: ACTIONS.SET_AMOUNT, payload: Number.isNaN(value) ? 0 : value });
  };
 
  const formatNumber = num => new Intl.NumberFormat().format(num);
 
  const buttonValues = [2000, 5000, 10000, 50000, 100000];
 
  const paymentTypeOptions = [
    { value: '1', label: 'Cash' },
    { value: '2', label: 'Online/UPI' },
    { value: '3', label: 'NEFT/RTGS/IMPS/Cheque' },
  ];
 
  const formatDateIST = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate())
      .padStart(2, '0')}`;
  };
 
  const addAmountHandler = async (values) => {
    // Check if payment type is selected
    if (!selectPaymentType) {
      toast.error('Please select payment type');
      return;
    }
 
    const paymentDate = values.receivedDate ? new Date(values.receivedDate) : new Date();
    const chequeDate = values.chequeDate ? new Date(values.chequeDate) : new Date();
   
    const formattedPaymentDate = formatDateIST(paymentDate);
    const formattedChequeDate = formatDateIST(chequeDate);
 
    if (!values.narration) {
      toast.error('Enter Narration');
      return;
    }
 
    const formData = new FormData();
    formData.append('restaurant_id', restaurantid);
    formData.append('user_id', userId);
    formData.append('out_amount', values.recAmount || amount);
   
    formData.append('narration', values.narration || '');
    formData.append('payment_type', selectPaymentType.value);
    formData.append('payment_date', formattedPaymentDate);
     
    if (selectPaymentType.value === '2') {
      if (!values.ref) {
        toast.error('Please enter Ref No');
        return;
      }
      formData.append('ref_no', values.ref);
    } else if (selectPaymentType.value === '3') {
      if (!values.ref) {
        toast.error('Please enter Ref No');
        return;
      }
      if (!values.bankName) {
        toast.error('Please enter Bank Name');
        return;
      }
      formData.append('ref_no', values.ref);
      formData.append('bank', values.bankName);
      formData.append('ref_date', formattedChequeDate);
      formData.append('cheque_date', formattedChequeDate);
    }
 
    try {
      const url = await fetch(`${apiGlobal}/agent/account_payment_add.php`, {
        method: 'POST',
        body: formData,
      });
      const response = await url.json();
     
      if (response.status && response.message === 'Success') {
        toast.success('Payment Done Successfully');
        setShowAddAmountModal(false);
      } else {
        toast.error(response.message || 'Payment Failed');
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to submit payment');
    }
  };
 
  useEffect(() => {
    // Update form values when amount changes to keep synchronized
    dispatch({
      type: ACTIONS.SET_FORM_VALUES,
      payload: { recAmount: amount },
    });
  }, [amount]);
 
  return (
    <div className="container mt-4">
      <div className="card-body">
        <div className="row mb-3">
          <div className="col-md-12">
            <label htmlFor="currentAmount" className="form-label">Current Amount</label>
            <input
              type="number"
              id="currentAmount"
              value={amount}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        </div>
         
        <div className="row mb-3">
          <div className="col-md-12">
            <p className="form-label">Quick Add</p>
            <div className="d-flex flex-wrap gap-2">
              {buttonValues.map(value => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleAddAmount(value)}
                  className="btn btn-primary"
                >
                  +{formatNumber(value)}
                </button>
                ))}
            </div>
          </div>
        </div>
         
        <div className="row">
          <div className="col-md-12">
            <div className="alert alert-info">
              <small>Total Amount:</small>
              <h3 className="mb-0">{formatNumber(amount)}</h3>
            </div>
          </div>
        </div>
       
      </div>
      <Form
        onSubmit={addAmountHandler}
        initialValues={formValues}
        subscription={{ submitting: true, pristine: true }}
      >
        {({ handleSubmit, form }) => (
          <FormContainer onSubmit={handleSubmit}>
            {/* Form Spy to keep track of form values without re-rendering */}
            <FormSpy
              subscription={{ values: true }}
              onChange={(formState) => {
                // Only track form values without causing re-renders
                dispatch({
                  type: ACTIONS.SET_FORM_VALUES,
                  payload: formState.values,
                });
              }}
            />
 
            <div className="row col-md-12">
              {/* Payment Date Field */}
              <div className="col-md-4">
                <FormGroup>
                  <FormGroupLabel>
                    Payment Date<span style={{ color: 'red' }}>*</span>
                  </FormGroupLabel>
                  <FormGroupField>
                    <Field
                      name="receivedDate"
                      render={({ input }) => (
                        <DatePicker
                          {...input}
                          selected={input.value ? new Date(input.value) : new Date()}
                          onChange={date => input.onChange(date)}
                          dateFormat="dd-MM-yyyy"
                          style={{
                            width: '271px',
                            height: '46px',
                          }}
                        />
                      )}
                      style={{
                        width: '271px',
                        height: '46px',
                      }}
                    />
                  </FormGroupField>
                </FormGroup>
              </div>
 
              <div className="col-md-4">
                <FormGroup>
                  <FormGroupLabel>
                    Received Amount<span style={{ color: 'red' }}>*</span>
                  </FormGroupLabel>
                  <FormGroupField>
                    <Field
                      name="recAmount"
                      component="input"
                      type="number"
                      style={{
                        width: '100%',
                        height: '46px',
                      }}
                      value={amount}
                      onChange={(e) => {
                        const newAmount = parseFloat(e.target.value);
                        dispatch({
                          type: ACTIONS.SET_AMOUNT,
                          payload: Number.isNaN(newAmount) ? 0 : newAmount,
                        });
                      }}
                    />
                  </FormGroupField>
                </FormGroup>
              </div>
              {/* Narration Field */}
              <div className="col-md-4">
                <FormGroup>
                  <FormGroupLabel>
                    Narration<span style={{ color: 'red' }}>*</span>
                  </FormGroupLabel>
                  <FormGroupField>
                    <Field
                      name="narration"
                      component="input"
                      type="text"
                      style={{
                        width: '100%',
                        height: '46px',
                      }}
                    />
                  </FormGroupField>
                </FormGroup>
              </div>
            </div>
 
            <div className="row col-md-12">
              <div className="col-md-4">
                <FormGroup>
                  <FormGroupLabel>
                    Payment Type<span style={{ color: 'red' }}>*</span>
                  </FormGroupLabel>
                  <div className="custom-select-wrapper">
                    <Select
                      className="react-select"
                      classNamePrefix="react-select"
                      options={paymentTypeOptions}
                      isClearable
                      placeholder="Payment Type"
                      value={selectPaymentType}
                      onChange={(option) => {
                        dispatch({ type: ACTIONS.SET_PAYMENT_TYPE, payload: option });
                      }}
                    />
                    {!selectPaymentType && (
                      <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>
                        Please select payment type
                      </div>
                    )}
                  </div>
                </FormGroup>
              </div>
 
              {/* Show Ref No for 'Online/UPI' (2) and 'NEFT/RTGS/IMPS/Cheque' (3) */}
              {selectPaymentType && ['2', '3'].includes(selectPaymentType.value) && (
                <div className="col-md-4">
                  <FormGroup>
                    <FormGroupLabel>
                      Ref No<span style={{ color: 'red' }}>*</span>
                    </FormGroupLabel>
                    <FormGroupField>
                      <Field
                        name="ref"
                        component="input"
                        type="text"
                        style={{ width: '100%', height: '46px' }}
                      />
                    </FormGroupField>
                  </FormGroup>
                </div>
              )}
 
              {/* Show Bank Name for 'NEFT/RTGS/IMPS/Cheque' (3) */}
              {selectPaymentType && selectPaymentType.value === '3' && (
                <div className="col-md-4">
                  <FormGroup>
                    <FormGroupLabel>
                      Bank Name<span style={{ color: 'red' }}>*</span>
                    </FormGroupLabel>
                    <FormGroupField>
                      <Field
                        name="bankName"
                        component="input"
                        type="text"
                        style={{ width: '100%', height: '46px' }}
                      />
                    </FormGroupField>
                  </FormGroup>
                </div>
              )}
            </div>
 
            {/* Show Cheque Date for 'NEFT/RTGS/IMPS/Cheque' (3) */}
            {selectPaymentType && selectPaymentType.value === '3' && (
              <div className="row col-md-12">
                <div className="col-md-4">
                  <FormGroup>
                    <FormGroupLabel>
                      Cheque Date<span style={{ color: 'red' }}>*</span>
                    </FormGroupLabel>
                    <FormGroupField>
                      <Field
                        name="chequeDate"
                        className="w-100"
                        render={({ input }) => (
                          <DatePicker
                            {...input}
                            selected={input.value ? new Date(input.value) : new Date()}
                            onChange={dates => input.onChange(dates)}
                            dateFormat="dd-MM-yyyy"
                            style={{
                              width: '100%',
                              height: '46px',
                            }}
                          />
                        )}
                        style={{
                          width: '271px',
                          height: '46px',
                        }}
                      />
                    </FormGroupField>
                  </FormGroup>
                </div>
              </div>
            )}
 
            <br />
            <FormButtonToolbar>
              <Button variant="primary" type="submit">
                Add Amount
              </Button>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setShowAddAmountModal(false)}
                style={{ marginLeft: '10px' }}
              >
                Cancel
              </Button>
            </FormButtonToolbar>
          </FormContainer>
        )}
      </Form>
    </div>
  );
};
 
AddAmount.propTypes = {
  setShowAddAmountModal: PropTypes.func.isRequired,
};
 
export default AddAmount;



// this is the original code before refactoring

// import React, { useContext, useState } from 'react';
// import { Field, Form } from 'react-final-form';
// import * as PropTypes from 'prop-types';
// import DatePicker from 'react-datepicker';
// import toast from 'react-hot-toast';
// import Select from 'react-select';
// import {
//   FormButtonToolbar,
//   FormContainer,
//   FormGroup,
//   FormGroupField,
//   FormGroupLabel,
// } from '@/shared/components/form/FormElements';
// import { Button } from '@/shared/components/Button';
// import { AuthContext } from '../../../../../../context/AuthContext';
// import { GlobalContext } from '../../../../../../context/GlobalContext';

// const AddAmount = ({ setShowAddAmountModal }) => {
//   const [amount, setAmount] = useState(0);
//   const [selectPaymentType, setSelectPaymentType] = useState(null);
//   const { apiGlobal } = useContext(GlobalContext);
//   const { agentId, userId } = useContext(AuthContext);
  
//   const handleAddAmount = (valueToAdd) => {
//     setAmount(prevAmount => prevAmount + valueToAdd);
//   };
  
//   const handleInputChange = (e) => {
//     const value = parseFloat(e.target.value);
//     setAmount(Number.isNaN(value) ? 0 : value);
//   };
  
//   const formatNumber = num => new Intl.NumberFormat().format(num);
  
//   const buttonValues = [2000, 5000, 10000, 50000, 100000];

//   const paymentTypeOptions = [
//     { value: '1', label: 'Cash' },
//     { value: '2', label: 'Online/UPI' },
//     { value: '3', label: 'NEFT/RTGS/IMPS/Cheque' },
//   ];

//   const formatDateIST = (date) => {
//     if (!date) return '';
//     return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate())
//       .padStart(2, '0')}`;
//   };

//   const addAmountHandler = async (value) => {
//     // Check if payment type is selected
//     if (!selectPaymentType) {
//       toast.error('Please select payment type');
//       return;
//     }

//     const paymentDate = value.receivedDate ? new Date(value.receivedDate) : new Date();
//     const chequeDate = value.chequeDate ? new Date(value.chequeDate) : new Date();
    
//     const formattedPaymentDate = formatDateIST(paymentDate);
//     const formattedChequeDate = formatDateIST(chequeDate);

//     if (!value.narration) {
//       toast.error('Enter Narration');
//       return;
//     }

//     const formData = new FormData();
//     formData.append('agent_id', agentId);
//     formData.append('user_id', userId);
//     formData.append('out_amount', value.recAmount || amount);
    
//     formData.append('narration', value.narration || '');
//     formData.append('payment_type', selectPaymentType.value);
//     formData.append('payment_date', formattedPaymentDate);

//   //   formData.forEach((values, key) => {
//   //     console.log(`${key}: ${values}`);
//   // });

      
//     if (selectPaymentType.value === '2') {
//       if (!value.ref) {
//         toast.error('Please enter Ref No');
//         return;
//       }
//       formData.append('ref_no', value.ref);
//     } else if (selectPaymentType.value === '3') {
//       if (!value.ref) {
//         toast.error('Please enter Ref No');
//         return;
//       }
//       if (!value.bankName) {
//         toast.error('Please enter Bank Name');
//         return;
//       }
//       formData.append('ref_no', value.ref);
//       formData.append('bank', value.bankName);
//       formData.append('ref_date', formattedChequeDate);
//       formData.append('cheque_date', formattedChequeDate);
//     }

//     try {
//       const url = await fetch(`${apiGlobal}/agent/account_payment_add.php`, {
//         method: 'POST',
//         body: formData,
//       });
//       const response = await url.json();
      
//       if (response.status && response.message === 'Success') {
//         toast.success('Payment Done Successfully');
//         setShowAddAmountModal(false);
//       } else {
//         toast.error(response.message || 'Payment Failed');
//       }
//     } catch (error) {
//       console.log(error);
//       toast.error('Failed to submit payment');
//     }
//   };
  
//   return (
//     <div className="container mt-4">
//       <div className="card-body">
//         <div className="row mb-3">
//           <div className="col-md-12">
//             <label htmlFor="currentAmount" className="form-label">Current Amount</label>
//             <input
//               type="number"
//               id="currentAmount"
//               value={amount}
//               onChange={handleInputChange}
//               className="form-control"
//             />
//           </div>
//         </div>
          
//         <div className="row mb-3">
//           <div className="col-md-12">
//             <p className="form-label">Quick Add</p>
//             <div className="d-flex flex-wrap gap-2">
//               {buttonValues.map(value => (
//                 <button
//                   key={value}
//                   type="button"
//                   onClick={() => handleAddAmount(value)}
//                   className="btn btn-primary"
//                 >
//                   +{formatNumber(value)}
//                 </button>
//                 ))}
//             </div>
//           </div>
//         </div>
          
//         <div className="row">
//           <div className="col-md-12">
//             <div className="alert alert-info">
//               <small>Total Amount:</small>
//               <h3 className="mb-0">{formatNumber(amount)}</h3>
//             </div>
//           </div>
//         </div>
        
//       </div>
//       <Form 
//         onSubmit={addAmountHandler} 
//         initialValues={{ 
//           recAmount: amount,
//           receivedDate: new Date(),
//           chequeDate: new Date(),
//         }}
//       >
//         {({ handleSubmit }) => (
//           <FormContainer onSubmit={handleSubmit}>
//             <div className="row col-md-12">
//               {/* Start Date Field */}
//               <div className="col-md-4">
//                 <FormGroup>
//                   <FormGroupLabel>
//                     Payment Date<span style={{ color: 'red' }}>*</span>
//                   </FormGroupLabel>
//                   <FormGroupField>
//                     <Field
//                       name="receivedDate"
//                       render={({ input }) => (
//                         <DatePicker 
//                           {...input}
//                           selected={input.value ? new Date(input.value) : new Date()}
//                           onChange={date => input.onChange(date)} 
//                           dateFormat="dd-MM-yyyy"
//                           style={{
//                             width: '271px',
//                             height: '46px',
//                         }}
//                         />
//                     )}
//                       style={{
//                       width: '271px',
//                       height: '46px',
//                     }}
//                     />
//                   </FormGroupField>
//                 </FormGroup>
//               </div>

//               <div className="col-md-4">
//                 <FormGroup>
//                   <FormGroupLabel>
//                     Received Amount<span style={{ color: 'red' }}>*</span>
//                   </FormGroupLabel>
//                   <FormGroupField>
//                     <Field
//                       name="recAmount"
//                       component="input"
//                       type="number"
//                       style={{
//                         width: '100%',
//                         height: '46px',
//                       }}
//                       initialValue={amount}
//                     />
//                   </FormGroupField>
//                 </FormGroup>
//               </div>
//               {/* Total Price Field (Optional) */}
//               <div className="col-md-4">
//                 <FormGroup>
//                   <FormGroupLabel>
//                     Narration<span style={{ color: 'red' }}>*</span>
//                   </FormGroupLabel>
//                   <FormGroupField>
//                     <Field
//                       name="narration"
//                       component="input"
//                       type="text"
//                       style={{
//                         width: '100%',
//                         height: '46px',
//                       }}
//                     />
//                   </FormGroupField>
//                 </FormGroup>
//               </div>
//             </div>
  
//             <div className="row col-md-12">
//               <div className="col-md-4">
//                 <FormGroup>
//                   <FormGroupLabel>
//                     Payment Type<span style={{ color: 'red' }}>*</span>
//                   </FormGroupLabel>
//                   <div className="custom-select-wrapper">
//                     <Select
//                       className="react-select"
//                       classNamePrefix="react-select"
//                       options={paymentTypeOptions}
//                       isClearable
//                       placeholder="Payment Type"
//                       value={selectPaymentType}
//                       onChange={option => setSelectPaymentType(option)}
//                     />
//                     {!selectPaymentType && (
//                       <div className="text-danger mt-1" style={{ fontSize: '0.8rem' }}>
//                         Please select payment type
//                       </div>
//                     )}
//                   </div>
//                 </FormGroup>
//               </div>

//               {/* Show Ref No for 'Online/UPI' (2) and 'NEFT/RTGS/IMPS/Cheque' (3) */}
//               {selectPaymentType && ['2', '3'].includes(selectPaymentType.value) && (
//                 <div className="col-md-4">
//                   <FormGroup>
//                     <FormGroupLabel>
//                       Ref No<span style={{ color: 'red' }}>*</span>
//                     </FormGroupLabel>
//                     <FormGroupField>
//                       <Field 
//                         name="ref" 
//                         component="input" 
//                         type="text" 
//                         style={{ width: '100%', height: '46px' }} 
//                       />
//                     </FormGroupField>
//                   </FormGroup>
//                 </div>
//               )}

//               {/* Show Bank Name for 'NEFT/RTGS/IMPS/Cheque' (3) */}
//               {selectPaymentType && selectPaymentType.value === '3' && (
//                 <div className="col-md-4">
//                   <FormGroup>
//                     <FormGroupLabel>
//                       Bank Name<span style={{ color: 'red' }}>*</span>
//                     </FormGroupLabel>
//                     <FormGroupField>
//                       <Field 
//                         name="bankName" 
//                         component="input" 
//                         type="text" 
//                         style={{ width: '100%', height: '46px' }} 
//                       />
//                     </FormGroupField>
//                   </FormGroup>
//                 </div>
//               )}
//             </div>

//             {/* Show Cheque Date for 'NEFT/RTGS/IMPS/Cheque' (3) */}
//             {selectPaymentType && selectPaymentType.value === '3' && (
//               <div className="row col-md-12">
//                 <div className="col-md-4">
//                   <FormGroup>
//                     <FormGroupLabel>
//                       Cheque Date<span style={{ color: 'red' }}>*</span>
//                     </FormGroupLabel>
//                     <FormGroupField>
//                       <Field
//                         name="chequeDate"
//                         className="w-100"
//                         render={({ input }) => (
//                           <DatePicker
//                             {...input}
//                             selected={input.value ? new Date(input.value) : new Date()}
//                             onChange={dates => input.onChange(dates)}
//                             dateFormat="dd-MM-yyyy"
//                             style={{
//                               width: '100%',
//                               height: '46px',
//                             }}
//                           />
//                         )}
//                         style={{
//                           width: '271px',
//                           height: '46px',
//                         }}
//                       />
//                     </FormGroupField>
//                   </FormGroup>
//                 </div>
//               </div>
//             )}

//             <br />
//             <FormButtonToolbar>
//               <Button variant="primary" type="submit">
//                 Add Amount
//               </Button>
//               <Button 
//                 variant="secondary" 
//                 type="button" 
//                 onClick={() => setShowAddAmountModal(false)}
//                 style={{ marginLeft: '10px' }}
//               >
//                 Cancel
//               </Button>
//             </FormButtonToolbar>
//           </FormContainer>
//         )}
//       </Form>
//     </div>
//   );
// };

// AddAmount.propTypes = {
//   setShowAddAmountModal: PropTypes.func.isRequired,
// };

// export default AddAmount;
